// Pure computations for the Test section (Daily Brief, Goals, Plan, Growth Score, Experiments). No React, no I/O.
// Every function takes the raw rows the BFF returns (metrics in cents, goals in display units) and returns plain values,
// so the pages and the demo view share one set of rules and one set of tests.

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

const utcDate = (dayKey) => {
  const [y, m, d] = dayKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const keyOf = (date) => date.toISOString().slice(0, 10);

/** Today's calendar day in the workspace's timezone (falls back to UTC on an unknown zone). */
export function todayKey(timeZone, now = new Date()) {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: timeZone || "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

export const isDayKey = (value) => typeof value === "string" && DAY_RE.test(value);
export const periodOf = (dayKey) => dayKey.slice(0, 7);
export const shiftDay = (dayKey, delta) => keyOf(new Date(utcDate(dayKey).getTime() + delta * DAY_MS));
export const daysBetween = (fromKey, toKey) => Math.round((utcDate(toKey) - utcDate(fromKey)) / DAY_MS);

/** Monday of the week that holds the day. */
export function weekStartOf(dayKey) {
  const date = utcDate(dayKey);
  const offset = (date.getUTCDay() + 6) % 7;
  return keyOf(new Date(date.getTime() - offset * DAY_MS));
}

/** The calendar month around a day: its bounds, how far in we are, and how much is left. */
export function monthWindow(dayKey) {
  const [y, m, d] = dayKey.split("-").map(Number);
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const start = `${dayKey.slice(0, 7)}-01`;
  const end = `${dayKey.slice(0, 7)}-${String(daysInMonth).padStart(2, "0")}`;
  // Sales are in through yesterday, so today still counts as a day left to sell.
  return { period: dayKey.slice(0, 7), start, end, daysInMonth, dayOfMonth: d, daysElapsed: d - 1, daysLeft: daysInMonth - d + 1 };
}

export const metricsBetween = (rows, start, end) => (rows || []).filter((row) => row.day >= start && row.day <= end);

/** Sum of a field over rows, ignoring nulls; `count` says how many days had a value. */
export function sumField(rows, field) {
  let total = 0;
  let count = 0;
  for (const row of rows || []) {
    const value = row?.[field];
    if (typeof value === "number" && Number.isFinite(value)) { total += value; count += 1; }
  }
  return { total, count };
}

export const GOAL_KINDS = Object.freeze({
  revenue: { label: "Revenue", unit: "usd", kind: "sum", direction: "up" },
  orders: { label: "Orders", unit: "count", kind: "sum", direction: "up" },
  new_customers: { label: "New customers", unit: "count", kind: "sum", direction: "up" },
  mer: { label: "Blended MER", unit: "ratio", kind: "ratio", direction: "up" },
  email_share: { label: "Email + SMS share", unit: "percent", kind: "ratio", direction: "up" },
  aov: { label: "Average order", unit: "usd", kind: "ratio", direction: "up" },
});

/** What the month has actually produced for a goal key, in the goal's display units, or null when unmeasured. */
export function goalActual(key, rows) {
  const revenue = sumField(rows, "revenueCents");
  const orders = sumField(rows, "orders");
  switch (key) {
    case "revenue": return revenue.count ? revenue.total / 100 : null;
    case "orders": return orders.count ? orders.total : null;
    case "new_customers": { const s = sumField(rows, "newCustomers"); return s.count ? s.total : null; }
    case "mer": {
      const spend = sumField(rows, "spendMetaCents").total + sumField(rows, "spendGoogleCents").total;
      return revenue.count && spend > 0 ? revenue.total / spend : null;
    }
    case "email_share": {
      const email = sumField(rows, "emailRevenueCents");
      const sms = sumField(rows, "smsRevenueCents");
      return revenue.total > 0 && (email.count || sms.count) ? ((email.total + sms.total) / revenue.total) * 100 : null;
    }
    case "aov": return revenue.count && orders.total > 0 ? revenue.total / orders.total / 100 : null;
    default: return null;
  }
}

/** Who set or approved something, for a client to read: the agent works under a service address nobody should see. */
export function setterName(value) {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return "unknown";
  if (/^brand-agent\+/i.test(text)) return "the agent";
  return text.includes("@") ? text.split("@")[0] : text;
}

const TARGET_MAX = 999_999_999_999;
/**
 * What an owner typed in a target box, read the way they meant it: "$50,000", "4x" and "35%" are numbers; a blank box
 * is no target. Anything else comes back with a sentence that says what to type instead, never a silent drop.
 */
export function parseTarget(raw, unit) {
  let text = String(raw ?? "").trim();
  if (!text) return { value: null, error: null };
  if (unit === "usd") text = text.replace(/^\$\s*/, "");
  if (unit === "percent") text = text.replace(/\s*%$/, "");
  if (unit === "ratio") text = text.replace(/\s*[x×]$/i, "");
  if (text.includes(",")) {
    if (!/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) return { value: null, error: "Use commas only between thousands, like 50,000." };
    text = text.replace(/,/g, "");
  }
  if (!/^\d+(\.\d+)?$/.test(text)) return { value: null, error: "Enter a number, like 50000." };
  const value = Number(text);
  if (!(value > 0)) return { value: null, error: "Enter a number above zero, or clear the box to remove this target." };
  if (unit === "count" && !Number.isInteger(value)) return { value: null, error: "Enter a whole number." };
  if (unit === "percent" && value > 100) return { value: null, error: "A share cannot be more than 100%." };
  if (value > TARGET_MAX) return { value: null, error: "That number is too large." };
  return { value, error: null };
}

/** A rounded starting point for a target from a measured value: what the store did, in numbers a person would type. */
export function suggestTarget(key, value) {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  const meta = GOAL_KINDS[key];
  if (!meta) return null;
  if (meta.unit === "ratio") return Math.round(value * 10) / 10;
  if (meta.unit === "percent") return Math.round(value);
  if (key === "aov") return Math.round(value);
  if (meta.unit === "usd") return value >= 20000 ? Math.round(value / 1000) * 1000 : value >= 2000 ? Math.round(value / 100) * 100 : Math.round(value / 10) * 10;
  return Math.max(1, Math.round(value / 10) * 10);
}

/**
 * The three unit-economics figures for the brief: average order from this month's metrics (the snapshot's 30-day figure when
 * the month has none), and the 12-month customer value and cost per new customer from the agent's scan snapshot.
 */
export function unitEconomicsFor({ monthRows, snapshot }) {
  const monthAov = goalActual("aov", monthRows);
  const aovCents = monthAov != null ? Math.round(monthAov * 100) : snapshot?.aovCents ?? null;
  return {
    aovCents,
    aovSource: monthAov != null ? "this month" : snapshot?.aovCents != null ? "last 30 days" : null,
    customerValue12mCents: snapshot?.customerValue12mCents ?? null,
    customerValue12mCohort: snapshot?.customerValue12mCohort ?? null,
    customerValue90dCents: snapshot?.customerValue90dCents ?? null,
    costPerNewCustomerCents: snapshot?.costPerNewCustomerCents ?? null,
    newCustomers30: snapshot?.newCustomers30 ?? null,
    spendCents30: snapshot?.spendCents30 ?? null,
    asOf: snapshot?.asOf ?? null,
  };
}

export const PACE_WORDS = Object.freeze({ met: "Met", on_pace: "On pace", slightly_behind: "Slightly behind", behind: "Behind", no_data: "No data yet" });
export const PACE_TONES = Object.freeze({ met: "ok", on_pace: "ok", slightly_behind: "warn", behind: "bad", no_data: "quiet" });

/**
 * Pace against a target. Sum goals (revenue, orders) extrapolate the month at the current daily rate; ratio goals (MER,
 * AOV, share) compare the value as it stands. "Slightly behind" is within 10% of the target.
 */
/** `daysElapsed` is the number of days whose figures are in (through yesterday); a sum is projected from those days only. */
export function paceFor({ actual, target, direction = "up", kind = "sum", daysElapsed, daysInMonth }) {
  if (actual == null || !Number.isFinite(actual) || !target || (kind === "sum" && !(daysElapsed > 0))) return { actual, target, percent: null, projection: null, status: "no_data", perDayNeeded: null };
  const projection = kind === "sum" ? (actual / daysElapsed) * daysInMonth : actual;
  const up = direction !== "down";
  const met = up ? actual >= target : actual <= target;
  const onPace = up ? projection >= target : projection <= target;
  const near = up ? projection >= target * 0.9 : projection <= target * 1.1;
  const status = met ? "met" : onPace ? "on_pace" : near ? "slightly_behind" : "behind";
  const daysLeft = daysInMonth - daysElapsed;
  const perDayNeeded = kind === "sum" && daysLeft > 0 && up ? Math.max(0, (target - actual) / daysLeft) : null;
  return { actual, target, percent: Math.min(999, (actual / target) * 100), projection, status, perDayNeeded };
}

/** One day against the average of the previous `days` days (needs three or more measured days). */
export function trailingComparison(rows, dayKey, field = "revenueCents", days = 7) {
  const row = (rows || []).find((entry) => entry.day === dayKey);
  const value = row?.[field] ?? null;
  const from = shiftDay(dayKey, -days);
  const prior = (rows || []).filter((entry) => entry.day >= from && entry.day < dayKey && typeof entry[field] === "number");
  if (value == null || prior.length < 3) return { value, average: null, deltaPercent: null };
  const average = prior.reduce((sum, entry) => sum + entry[field], 0) / prior.length;
  return { value, average, deltaPercent: average > 0 ? ((value - average) / average) * 100 : null };
}

/** Values of a field for the seven days ending on the day, oldest first (nulls stay null so a chart can skip them). */
export function trailingValues(rows, dayKey, field = "revenueCents", days = 7) {
  const byDay = new Map((rows || []).map((row) => [row.day, row]));
  return Array.from({ length: days }, (_, index) => byDay.get(shiftDay(dayKey, index - days + 1))?.[field] ?? null);
}

/** Cumulative total of a field from the month start to each measured day. */
export function cumulativeSeries(rows, field, start, end) {
  let running = 0;
  return metricsBetween(rows, start, end)
    .slice()
    .sort((a, b) => (a.day < b.day ? -1 : 1))
    .filter((row) => typeof row[field] === "number")
    .map((row) => { running += row[field]; return { day: row.day, value: running }; });
}

/** The straight line from zero to the target across the month, one point per day. */
export const neededSeries = (target, start, daysInMonth) =>
  Array.from({ length: daysInMonth }, (_, index) => ({ day: shiftDay(start, index), value: (target * (index + 1)) / daysInMonth }));

export const INITIATIVE_STATUS = Object.freeze({
  next: { label: "Next", tone: "quiet" },
  in_progress: { label: "In progress", tone: "busy" },
  waiting_on_you: { label: "Waiting on you", tone: "warn" },
  done: { label: "Done", tone: "ok" },
  parked: { label: "Parked", tone: "quiet" },
});
export const EXPERIMENT_STATUS = Object.freeze({
  idea: { label: "Idea", tone: "quiet" },
  running: { label: "Running", tone: "busy" },
  decided: { label: "Decided", tone: "ok" },
  dropped: { label: "No difference", tone: "quiet" },
});
const OPEN_INITIATIVES = new Set(["next", "in_progress", "waiting_on_you"]);

/**
 * Where a revenue goal's number would come from: the month's run-rate, each open initiative's expected impact, and the gap.
 * Only usd sum goals have a bar; other goals return `parts: []`.
 */
export function contributionsFor({ goal, pace, initiatives }) {
  const attached = (initiatives || []).filter((item) => item.goalKey === goal.key);
  const meta = GOAL_KINDS[goal.key];
  if (!meta || meta.unit !== "usd" || meta.kind !== "sum" || pace?.projection == null) return { attached, parts: [], base: pace?.projection ?? null, planned: 0, gap: null };
  const base = pace.projection;
  const planned = attached.filter((item) => OPEN_INITIATIVES.has(item.status) && (item.expectedImpactCents || 0) > 0);
  const plannedTotal = planned.reduce((sum, item) => sum + (item.expectedImpactCents || 0) / 100, 0);
  const gap = Math.max(0, goal.target - base - plannedTotal);
  const scale = Math.max(goal.target, base + plannedTotal);
  const percent = (amount) => (scale > 0 ? (amount / scale) * 100 : 0);
  const parts = [
    { key: "base", label: "Run-rate", amount: base, percent: percent(base), kind: "base" },
    ...planned.map((item) => ({ key: item.id, label: item.title, amount: (item.expectedImpactCents || 0) / 100, percent: percent((item.expectedImpactCents || 0) / 100), kind: "initiative" })),
    ...(gap > 0 ? [{ key: "gap", label: "Gap", amount: gap, percent: percent(gap), kind: "gap" }] : []),
  ];
  return { attached, parts, base, planned: plannedTotal, gap };
}

/** Counts and progress for the plan. */
export function planSummary(initiatives) {
  const list = initiatives || [];
  const count = (status) => list.filter((item) => item.status === status).length;
  const active = list.filter((item) => item.status !== "parked");
  const done = count("done");
  return {
    total: active.length,
    done,
    inProgress: count("in_progress"),
    waiting: count("waiting_on_you"),
    next: count("next"),
    parked: count("parked"),
    expectedImpactCents: active.reduce((sum, item) => sum + (item.expectedImpactCents || 0), 0),
    progressPercent: active.length ? Math.round((done / active.length) * 100) : 0,
  };
}

/** Initiatives that belong on this week's agenda: due or scheduled this week, or already moving. */
export function initiativesForWeek(initiatives, dayKey) {
  const start = weekStartOf(dayKey);
  const end = shiftDay(start, 6);
  return (initiatives || []).filter((item) => {
    if (item.status === "done" || item.status === "parked") return false;
    if (item.status === "in_progress" || item.status === "waiting_on_you") return true;
    const when = item.due || item.weekStart;
    return Boolean(when) && when >= start && when <= end;
  });
}

/** Score runs paired with their predecessor: the delta and the rubric lines whose points changed. */
export function scoreDiffs(runs) {
  const list = (runs || []).slice().sort((a, b) => (a.scoredAt < b.scoredAt ? -1 : 1));
  return list.slice(1).map((run, index) => {
    const previous = list[index];
    const before = new Map((previous.lines || []).map((line) => [line.id, line]));
    const moved = (run.lines || [])
      .map((line) => { const old = before.get(line.id); const from = old?.earned ?? 0; const to = line.earned ?? 0; return { id: line.id, label: line.label, half: line.half, from, to, delta: to - from }; })
      .filter((line) => line.delta !== 0)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    return { run, previous, delta: (run.score ?? 0) - (previous.score ?? 0), moved };
  }).reverse();
}

export const latestOpportunities = (run, limit = 3) => (run?.opportunities || []).slice().sort((a, b) => b.pointsLost - a.pointsLost).slice(0, limit);

export const CHANNEL_GROUPS = Object.freeze(["Ads", "Email & SMS", "Site & offers", "Other"]);
const channelGroup = (channel) => {
  const value = String(channel || "").toLowerCase();
  if (/meta|google|tiktok|ads|paid/.test(value)) return "Ads";
  if (/email|sms|klaviyo|attentive|postscript/.test(value)) return "Email & SMS";
  if (/site|shopify|page|offer|checkout|bundle|price/.test(value)) return "Site & offers";
  return "Other";
};

/** Decided or dropped tests grouped by channel family, newest first. */
export function lessonsByChannel(experiments) {
  const groups = new Map(CHANNEL_GROUPS.map((name) => [name, []]));
  for (const test of (experiments || []).filter((item) => item.status === "decided" || item.status === "dropped")) groups.get(channelGroup(test.channel)).push(test);
  for (const list of groups.values()) list.sort((a, b) => ((b.decidedOn || b.updatedAt) < (a.decidedOn || a.updatedAt) ? -1 : 1));
  return [...groups.entries()].filter(([, list]) => list.length).map(([name, list]) => ({ name, tests: list }));
}

export const ownerWord = (owner, agentName) => (owner === "client" ? "You" : owner === "both" ? `You + ${agentName}` : agentName);

// ---- formatting ----
const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num0 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
export const fmtUsd = (dollars, { cents = false } = {}) => (dollars == null || !Number.isFinite(dollars) ? "—" : (cents ? usd2 : usd0).format(dollars));
export const fmtUsdCents = (value, options) => (value == null ? "—" : fmtUsd(value / 100, options));
export const fmtCount = (value) => (value == null || !Number.isFinite(value) ? "—" : num0.format(value));
export const fmtRatio = (value) => (value == null || !Number.isFinite(value) ? "—" : `${value.toFixed(1)}×`);
export const fmtPercent = (value, digits = 0) => (value == null || !Number.isFinite(value) ? "—" : `${value.toFixed(digits)}%`);
export const fmtDelta = (percent) => (percent == null ? "" : `${percent >= 0 ? "+" : "−"}${Math.abs(percent).toFixed(0)}%`);
export function fmtByUnit(value, unit) {
  if (unit === "usd") return fmtUsd(value, { cents: value != null && Math.abs(value) < 1000 && !Number.isInteger(value) });
  if (unit === "ratio") return fmtRatio(value);
  if (unit === "percent") return fmtPercent(value);
  return fmtCount(value);
}
export const fmtTarget = (goal) => (goal.unit === "ratio" || goal.unit === "percent" ? `${goal.direction === "down" ? "≤" : "≥"} ${fmtByUnit(goal.target, goal.unit)}` : fmtByUnit(goal.target, goal.unit));
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function fmtDayShort(dayKey) { if (!isDayKey(dayKey)) return "—"; const d = utcDate(dayKey); return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`; }
export function fmtDayLong(dayKey) { if (!isDayKey(dayKey)) return "—"; const d = utcDate(dayKey); return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`; }
export function fmtMonth(period) { const [y, m] = String(period || "").split("-").map(Number); return m ? `${["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][m - 1]} ${y}` : "—"; }
export const fmtRange = (start, end) => (isDayKey(start) && isDayKey(end) ? `${fmtDayShort(start)} to ${fmtDayShort(end)}` : "—");
export function fmtStamp(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** The brief feed: the last `days` days that have a brief or a metric, newest first, each with its comparison and a sparkline. */
export function briefFeed({ briefs, metrics, dayKey, days = 14, compare = 7 }) {
  const briefByDay = new Map((briefs || []).map((brief) => [brief.day, brief]));
  const metricByDay = new Map((metrics || []).map((row) => [row.day, row]));
  const entries = [];
  for (let index = 1; index <= days; index += 1) {
    const day = shiftDay(dayKey, -index);
    const brief = briefByDay.get(day) || null;
    const metric = metricByDay.get(day) || null;
    if (!brief && !metric) continue;
    entries.push({ day, brief, metric, comparison: trailingComparison(metrics, day, "revenueCents", compare), spark: trailingValues(metrics, day, "revenueCents", 7) });
  }
  return entries;
}

/** Everything on the agenda for today and this week. */
export function agendaFor({ initiatives, questions, automations, dayKey, agentName, timezone }) {
  const items = [];
  const weekStart = weekStartOf(dayKey);
  for (const item of initiativesForWeek(initiatives, dayKey)) {
    const when = item.due || item.weekStart;
    items.push({ kind: "initiative", ref: item.id, title: item.title, text: item.status === "waiting_on_you" ? "Waiting on you" : `${ownerWord(item.owner, agentName)} · ${INITIATIVE_STATUS[item.status]?.label || item.status}`, tone: INITIATIVE_STATUS[item.status]?.tone || "quiet", today: when === dayKey || item.status === "waiting_on_you", when });
  }
  const open = (questions || []).filter((question) => question.status === "open");
  if (open.length) items.push({ kind: "questions", ref: null, title: `${open.length} question${open.length === 1 ? "" : "s"} for you`, text: `${agentName} needs facts only you know`, tone: "warn", today: true, when: dayKey });
  for (const job of automations || []) {
    const next = job.nextRunAt ? new Date(job.nextRunAt) : null;
    const nextKey = next && !Number.isNaN(next.getTime()) ? todayKey(timezone, next) : null;
    if (!nextKey || nextKey < dayKey || nextKey > shiftDay(weekStart, 6)) continue;
    items.push({ kind: "automation", ref: job.id, title: job.name || "Scheduled automation", text: job.schedule ? `${agentName} · ${job.schedule}` : agentName, tone: "quiet", today: nextKey === dayKey, when: nextKey });
  }
  // Today first; within a day, things to decide before questions before scheduled jobs; then by date.
  const rank = { initiative: 0, questions: 1, automation: 2 };
  items.sort((a, b) => (a.today !== b.today ? (a.today ? -1 : 1) : rank[a.kind] !== rank[b.kind] ? rank[a.kind] - rank[b.kind] : a.when < b.when ? -1 : a.when > b.when ? 1 : 0));
  return { today: items.filter((item) => item.today), week: items.filter((item) => !item.today) };
}
